import { PROJECTS, CASES, BIO_FACTS } from './data.js';
import { onNavClick } from './router.js';

export function Hero() {
  return (
    <section className="hero">
      <div className="container">
        <div className="hero__eyebrow">
          <span>Fractional Chief Product Designer</span>
          <span className="hero__eyebrow-sep">·</span>
          <span>The Woodlands, TX → Remote</span>
        </div>

        <h1 className="hero__headline">
          <span className="line"><span>End&#8209;to&#8209;end</span></span>
          <span className="line"><span>product design <span className="amp">&amp;</span></span></span>
          <span className="line"><span className="accent">experience strategy.</span></span>
        </h1>

        <p className="hero__intro">
          Twenty years shaping retail, commerce, and healthcare experiences for
          Dick's Sporting Goods, Home Depot, Mattress Firm, and beyond.
          Embedded where design meets the business.
        </p>

        <div className="hero__cta">
          <a href="#work" className="link-mono">View work ↓</a>
          <span className="sep">·</span>
          <a href="mailto:ryanmarfone@gmail.com" className="link-mono">Get in touch →</a>
        </div>
      </div>
    </section>
  );
}

export function Work() {
  return (
    <section className="work" id="work">
      <div className="container">
        <header className="sec-head">
          <span className="sec-head__num">01 · Work</span>
          <h2 className="sec-head__title">Portfolio</h2>
          <span className="sec-head__meta">{PROJECTS.length} engagements</span>
        </header>
        <div className="work__grid">
          {PROJECTS.map(p => {
            const hasCase = !!CASES[p.id];
            const href    = hasCase ? `/case/${p.id}` : '#contact';
            const onClick = hasCase ? onNavClick : undefined;
            return (
              <a key={p.id} href={href} onClick={onClick} className={"work__card" + (p.tile.dark ? ' work__card--dark' : '')}>
                <div className="work__art" style={{ background: p.tile.bg }}>
                  {p.tile.image && <img src={p.tile.image} alt="" className="work__art-img" style={p.tile.fit ? { objectFit: p.tile.fit } : undefined} loading="lazy" />}
                </div>
                <div className="work__body">
                  <div className="work__meta">
                    <span>{p.num}</span>
                    <span>{p.tag}</span>
                  </div>
                  <h3 className="work__title">{p.title}</h3>
                  <p className="work__summary">{p.summary}</p>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function About() {
  return (
    <section className="about" id="about">
      <div className="container">
        <header className="sec-head">
          <span className="sec-head__num">02 · About</span>
          <h2 className="sec-head__title">About</h2>
          <span className="sec-head__meta">Fractional CPD</span>
        </header>
        <div className="about__grid">
          <aside className="about__side">
            <figure className="about__portrait">
              <img src="/ryan.jpg" alt="Ryan Marfone" loading="lazy" />
            </figure>
            <dl className="bio-facts">
              {BIO_FACTS.map(f => (
                <div key={f.k} className="bio-facts__row">
                  <dt>{f.k}</dt>
                  <dd>{f.v}</dd>
                </div>
              ))}
            </dl>
          </aside>
          <div className="about__body">
            <p className="about__lead">I've spent twenty years embedded inside product, service, and experience teams, shaping the work from first principles to shipped pixels.</p>
            <p>My best work sits at the seam where design, engineering, and business strategy meet. I translate ambiguous ambitions into concrete interfaces, systems, and operating models, plus the rituals that keep them alive after I leave.</p>
            <p>I work in small, senior configurations, most often as a fractional Chief Product Designer. I'm most useful to teams in the first 90 days of a zero-to-one, or in the quarter before a major redesign ships.</p>
            <p>Lately I'm focused on how AI and machine learning reshape product design: agentic interfaces, generative workflows, and the new patterns emerging as teams build with large language models. I help product teams translate these capabilities into experiences people actually want to use.</p>
            <div className="about__meta">
              <div>
                <h4>Practice</h4>
                <ul>
                  <li>Product Design</li>
                  <li>Service Design</li>
                  <li>Experience Strategy</li>
                  <li>AI &amp; Machine Learning Product Design</li>
                  <li>Generative AI &amp; Agentic UX</li>
                  <li>Design Systems</li>
                  <li>Prototyping &amp; Engineering</li>
                  <li>Workshop Facilitation</li>
                </ul>
              </div>
              <div>
                <h4>Sectors</h4>
                <ul>
                  <li>Retail &amp; Commerce</li>
                  <li>Healthcare &amp; Pharma</li>
                  <li>Insurance &amp; Fintech</li>
                  <li>Consumer Mobile</li>
                  <li>AI &amp; ML Products</li>
                  <li>Emerging Technology</li>
                  <li>Home &amp; Lifestyle</li>
                  <li>Enterprise SaaS</li>
                </ul>
              </div>
              <div>
                <h4>Engagements</h4>
                <ul>
                  <li>Fractional CPD</li>
                  <li>Design Sprint Leadership</li>
                  <li>Zero-to-One Partnership</li>
                  <li>AI Product Discovery</li>
                  <li>Team Coaching</li>
                  <li>Executive Workshops</li>
                  <li>Advisory</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Contact() {
  return (
    <section className="contact" id="contact">
      <div className="container">
        <header className="sec-head">
          <span className="sec-head__num">03 · Contact</span>
          <h2 className="sec-head__title">Let's talk</h2>
          <span className="sec-head__meta">Usually reply within a day</span>
        </header>
        <div className="contact__body">
          <p className="contact__lead">Open to senior design leadership roles, fractional CPD engagements, and selected advisory.</p>
          <a href="mailto:ryanmarfone@gmail.com" className="contact__email">
            <span>ryanmarfone@gmail.com</span><span>→</span>
          </a>
          <div className="contact__mini">
            <a href="https://www.linkedin.com/in/ryanmarfone/hello">LinkedIn ↗</a>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__row">
          <div>Ryan Marfone</div>
          <div className="footer__center">www.ryanmarfone.com</div>
          <div className="footer__right">The Woodlands, TX</div>
        </div>
      </div>
    </footer>
  );
}
